import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';

// material-ui
import {
  Checkbox, FormControl, ListItemText, Button, Paper, MenuItem, Typography, Box
} from '@mui/material';

// ==============================|| COLUMN VISIBILITY - SELECT ||============================== //

const SelectColumnVisibility = ({ 
  getVisibleLeafColumns, getIsAllColumnsVisible, getToggleAllColumnsVisibilityHandler, getAllColumns, totalColumns
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };

    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [open]);

  const visibleCount = getVisibleLeafColumns().length;
  const displayText = getIsAllColumnsVisible() 
    ? 'All columns visible'
    : visibleCount === 0
    ? 'All columns hidden'
    : `${totalColumns ? `${visibleCount}/${totalColumns}` : visibleCount} column(s) visible`;

  return (
    <FormControl sx={{ width: 220, position: 'relative' }} ref={wrapperRef}>
      <Button
        ref={buttonRef}
        onClick={handleClick}
        variant="outlined"
        size="small"
        sx={{
          width: '100%',
          height: '45px',
          minHeight: '45px',
          justifyContent: 'space-between',
          textTransform: 'none',
          color: 'text.primary',
          borderColor: 'rgb(27 172 39 / 85.6%)',
          borderRadius: '10px',
          borderWidth: '2px',
          '&:hover': { 
            borderColor: 'rgb(16 196 91)',
            borderWidth: '2px'
          },
          paddingLeft: '14px',
          paddingRight: '14px',
          fontSize: '0.875rem',
          fontWeight: 600
        }}
      >
        <Typography variant="subtitle1" sx={{ fontSize: '0.875rem', flex: 1, textAlign: 'left', fontWeight: 400 }}>
          {displayText}
        </Typography>
        <Box sx={{ ml: 1, fontSize: '0.75rem' }}>▼</Box>
      </Button>

      {open && (
        <Paper
          sx={{
            width: '100%',
            maxHeight: 300,
            overflowY: 'auto',
            border: '1px solid #ccc',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 9999,
            backgroundColor: '#fff',
            mt: '8px'
          }}
        >
          <MenuItem
            onClick={(e) => {
              const handler = getToggleAllColumnsVisibilityHandler();
              if (typeof handler === 'function') handler(e);
              handleClose();
            }}
          >
            <Checkbox checked={getIsAllColumnsVisible()} color="success" />
            <ListItemText primary="All Column" />
          </MenuItem>
          {getAllColumns().map(
            (column) =>
              // @ts-ignore
              column.columnDef.accessorKey && (
                <MenuItem
                  key={column.id}
                  onClick={(e) => {
                    const handler = column.getToggleVisibilityHandler();
                    if (typeof handler === 'function') handler(e);
                  }}
                >
                  <Checkbox checked={column.getIsVisible()} color="success" />
                  <ListItemText primary={column.columnDef.header} />
                </MenuItem>
              )
          )}
        </Paper>
      )}
    </FormControl>
  );
};

SelectColumnVisibility.propTypes = {
  getVisibleLeafColumns: PropTypes.func,
  getIsAllColumnsVisible: PropTypes.func,
  getToggleAllColumnsVisibilityHandler: PropTypes.func,
  getAllColumns: PropTypes.func,
  totalColumns: PropTypes.number
};

export default SelectColumnVisibility;
